import { lazy, Suspense } from "react";
import { Navigate, Routes, Route } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { FeatureRoute } from "@/components/common/FeatureRoute";
import { CustomPlanFeatureRoute } from "@/components/common/CustomPlanFeatureRoute";
import { CustomPlanRedirect } from "./CustomPlanRedirect";
import { CategoryRoutes } from "./CategoryRoutes";
import { ContractRoutes } from "./ContractRoutes";
import { ContractTypeRoutes } from "./ContractTypeRoutes";
import { CurrencyRoutes } from "./CurrencyRoutes";
import { ContactRoutes } from "./ContactRoutes";
import { GroupRoutes } from "./GroupRoutes";
import { AdminRoutes } from "./AdminRoutes";
import { ServiceRoutes } from "./ServiceRoutes";
import { DocumentRoutes } from "./DocumentRoutes";
import { SupplierRoutes } from "./SupplierRoutes";
import { ContractDocumentRoutes } from "./ContractDocumentRoutes";
import { ApiPortalRoutes } from "./ApiPortalRoutes";
import { GoverningDocumentRoutes } from "./GoverningDocumentRoutes";
import { LazyLoadFallback } from "./LazyLoadFallback";
import { useAuth } from "@/hooks/useAuth";

// Lazy-loaded pages
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const SettingsPage = lazy(() => import("@/pages/settings/SettingsPage"));
const IntegrationsPage = lazy(() => import("@/pages/settings/IntegrationsPage"));
const CustomPlanPage = lazy(() => import("@/pages/settings/CustomPlanPage"));
const CustomFieldsPage = lazy(() => import("@/pages/settings/CustomFieldsPage"));
const TagsPage = lazy(() => import("@/pages/settings/TagsPage"));
const DocumentationPage = lazy(() => import("@/pages/DocumentationPage"));
const TripletexSetupPage = lazy(() => import("@/pages/erp-integration/TripletexSetupPage"));
const ExpenseDetectionPage = lazy(() => import("@/pages/erp-integration/ExpenseDetectionPage"));
const FinagoSetupPage = lazy(() => import("@/pages/erp-integration/FinagoSetupPage"));
const FinagoCallbackPage = lazy(() => import("@/pages/erp-integration/FinagoCallbackPage"));
const PendingContractsPage = lazy(() => import("@/pages/erp-integration/PendingContractsPage"));
const FinagoExpenseDetectionPage = lazy(() => import("@/pages/erp-integration/FinagoExpenseDetectionPage"));
const PowerOfficeSetupPage = lazy(() => import("@/pages/erp-integration/PowerOfficeSetupPage"));
const PowerOfficeCallbackPage = lazy(() => import("@/pages/erp-integration/PowerOfficeCallbackPage"));
const PowerOfficeExpenseDetectionPage = lazy(() => import("@/pages/erp-integration/PowerOfficeExpenseDetectionPage"));
const BusinessCentralSetupPage = lazy(() => import("@/pages/erp-integration/BusinessCentralSetupPage"));
const BusinessCentralCallbackPage = lazy(() => import("@/pages/erp-integration/BusinessCentralCallbackPage"));
const BusinessCentralExpenseDetectionPage = lazy(() => import("@/pages/erp-integration/BusinessCentralExpenseDetectionPage"));
const ApprovalWorkflowsPage = lazy(() => import("@/pages/ApprovalWorkflowsPage"));
const CostOptimizationPage = lazy(() => import("@/pages/CostOptimizationPage"));
const SalesAnalyticsPage = lazy(() => import("@/pages/SalesAnalyticsPage"));
const DataHealthPage = lazy(() => import("@/pages/DataHealthPage"));
const PortfolioAssessmentPage = lazy(() => import("@/pages/PortfolioAssessmentPage"));
const SharedReportPage = lazy(() => import("@/pages/SharedReportPage"));
const AcceptAdvisorInvitationPage = lazy(() => import("@/pages/advisor/AcceptAdvisorInvitationPage"));
const AdvisorDashboardPage = lazy(() => import("@/pages/advisor/AdvisorDashboardPage"));
const BureauCockpitPage = lazy(() => import("@/pages/advisor/BureauCockpitPage"));
const Login = lazy(() => import("@/pages/auth/Login"));
const Register = lazy(() => import("@/pages/auth/Register"));
const PlanSelection = lazy(() => import("@/pages/auth/PlanSelection"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const IndexPage = lazy(() => import("@/pages/IndexPage"));
const InvestorPitchPage = lazy(() => import("@/pages/InvestorPitchPage"));
const CustomerPitchPage = lazy(() => import("@/pages/CustomerPitchPage"));
const PartnerPitchPage = lazy(() => import("@/pages/PartnerPitchPage"));
const PublicCustomPlanPage = lazy(() => import("@/pages/PublicCustomPlanPage"));
const CustomCheckoutPage = lazy(() => import("@/pages/subscription/CustomCheckoutPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const SecurityPage = lazy(() => import("@/pages/SecurityPage"));
const AcceptInvitationPage = lazy(() => import("@/pages/invitation/AcceptInvitationPage"));
const ImpersonateCallbackPage = lazy(() => import("@/pages/ImpersonateCallbackPage"));

export function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Suspense fallback={<LazyLoadFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={isAuthenticated ? <Navigate to="/contracts" replace /> : <IndexPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/auth" element={<Navigate to="/login" replace />} />
        <Route path="/choose-plan" element={<PlanSelection />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/invitation/accept" element={<AcceptInvitationPage />} />
        <Route path="/shared-report/:token" element={<SharedReportPage />} />
        <Route path="/advisor/accept" element={<AcceptAdvisorInvitationPage />} />
        <Route path="/impersonate-callback" element={<ImpersonateCallbackPage />} />
        <Route path="/subscription/custom" element={<PublicCustomPlanPage />} />
        <Route path="/investor-pitch" element={<InvestorPitchPage />} />
        <Route path="/customer-pitch" element={<CustomerPitchPage />} />
        <Route path="/partner-pitch" element={<PartnerPitchPage />} />
        <Route path="/subscription/checkout" element={<ProtectedRoute><CustomCheckoutPage /></ProtectedRoute>} />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <CustomPlanRedirect>
              <DashboardPage />
            </CustomPlanRedirect>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/settings/subscription" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/settings/integrations" element={
          <ProtectedRoute>
            <CustomPlanRedirect>
              <IntegrationsPage />
            </CustomPlanRedirect>
          </ProtectedRoute>
        } />
        <Route path="/settings/custom-fields" element={
          <ProtectedRoute>
            <CustomPlanRedirect>
              <FeatureRoute feature="custom_fields" featureName="Custom Fields">
                <CustomFieldsPage />
              </FeatureRoute>
            </CustomPlanRedirect>
          </ProtectedRoute>
        } />
        <Route path="/settings/tags" element={
          <ProtectedRoute>
            <CustomPlanRedirect>
              <TagsPage />
            </CustomPlanRedirect>
          </ProtectedRoute>
        } />
        <Route path="/settings/subscription/custom" element={<ProtectedRoute><CustomPlanPage /></ProtectedRoute>} />
        <Route path="/advisor/dashboard" element={<ProtectedRoute><AdvisorDashboardPage /></ProtectedRoute>} />
        <Route path="/advisor/cockpit" element={<ProtectedRoute><BureauCockpitPage /></ProtectedRoute>} />
        <Route path="/documentation" element={<ProtectedRoute><DocumentationPage /></ProtectedRoute>} />
        <Route path="/approval-workflows" element={
          <ProtectedRoute>
            <CustomPlanRedirect>
              <FeatureRoute feature="sales_analytics" featureName="Approval Workflows">
                <ApprovalWorkflowsPage />
              </FeatureRoute>
            </CustomPlanRedirect>
          </ProtectedRoute>
        } />
        <Route path="/sales-analytics" element={
          <ProtectedRoute>
            <CustomPlanRedirect>
              <FeatureRoute feature="sales_analytics" featureName="Sales Analytics">
                <SalesAnalyticsPage />
              </FeatureRoute>
            </CustomPlanRedirect>
          </ProtectedRoute>
        } />
        <Route path="/cost-optimization" element={
          <ProtectedRoute>
            <CustomPlanRedirect>
              <FeatureRoute feature="cost_optimization" featureName="Cost Optimization">
                <CostOptimizationPage />
              </FeatureRoute>
            </CustomPlanRedirect>
          </ProtectedRoute>
        } />
        <Route path="/data-health" element={
          <ProtectedRoute>
            <CustomPlanRedirect>
              <FeatureRoute feature="data_health" featureName="Data Health">
                <DataHealthPage />
              </FeatureRoute>
            </CustomPlanRedirect>
          </ProtectedRoute>
        } />
        <Route path="/portfolio-assessment" element={
          <ProtectedRoute>
            <CustomPlanRedirect>
              <FeatureRoute feature="data_health" featureName="Portfolio Assessment">
                <PortfolioAssessmentPage />
              </FeatureRoute>
            </CustomPlanRedirect>
          </ProtectedRoute>
        } />

        {/* ERP Integration routes */}
        <Route path="/erp-integration/tripletex/setup" element={
          <ProtectedRoute><CustomPlanRedirect><FeatureRoute feature="erp_integration" featureName="ERP Integration">
            <TripletexSetupPage />
          </FeatureRoute></CustomPlanRedirect></ProtectedRoute>
        } />
        <Route path="/erp-integration/tripletex/expenses" element={
          <ProtectedRoute><CustomPlanRedirect><FeatureRoute feature="erp_integration" featureName="ERP Integration">
            <CustomPlanFeatureRoute requiredFeatures={['erp_integration', 'contract_management']} featureName="Contract Management">
              <ExpenseDetectionPage />
            </CustomPlanFeatureRoute>
          </FeatureRoute></CustomPlanRedirect></ProtectedRoute>
        } />
        <Route path="/erp-integration/finago/setup" element={
          <ProtectedRoute><CustomPlanRedirect><FeatureRoute feature="erp_integration" featureName="ERP Integration">
            <FinagoSetupPage />
          </FeatureRoute></CustomPlanRedirect></ProtectedRoute>
        } />
        <Route path="/erp-integration/finago/callback" element={
          <ProtectedRoute><CustomPlanRedirect><FeatureRoute feature="erp_integration" featureName="ERP Integration">
            <FinagoCallbackPage />
          </FeatureRoute></CustomPlanRedirect></ProtectedRoute>
        } />
        <Route path="/erp-integration/finago/expenses" element={
          <ProtectedRoute><CustomPlanRedirect><FeatureRoute feature="erp_integration" featureName="ERP Integration">
            <CustomPlanFeatureRoute requiredFeatures={['erp_integration', 'contract_management']} featureName="Contract Management">
              <FinagoExpenseDetectionPage />
            </CustomPlanFeatureRoute>
          </FeatureRoute></CustomPlanRedirect></ProtectedRoute>
        } />
        <Route path="/erp-integration/pending-contracts" element={
          <ProtectedRoute><CustomPlanRedirect><FeatureRoute feature="erp_integration" featureName="ERP Integration">
            <CustomPlanFeatureRoute requiredFeatures={['erp_integration', 'contract_management']} featureName="Contract Management">
              <PendingContractsPage />
            </CustomPlanFeatureRoute>
          </FeatureRoute></CustomPlanRedirect></ProtectedRoute>
        } />
        <Route path="/erp-integration/poweroffice/setup" element={
          <ProtectedRoute><CustomPlanRedirect><FeatureRoute feature="erp_integration" featureName="ERP Integration">
            <PowerOfficeSetupPage />
          </FeatureRoute></CustomPlanRedirect></ProtectedRoute>
        } />
        <Route path="/erp-integration/poweroffice/callback" element={
          <ProtectedRoute><CustomPlanRedirect><FeatureRoute feature="erp_integration" featureName="ERP Integration">
            <PowerOfficeCallbackPage />
          </FeatureRoute></CustomPlanRedirect></ProtectedRoute>
        } />
        <Route path="/erp-integration/poweroffice/expenses" element={
          <ProtectedRoute><CustomPlanRedirect><FeatureRoute feature="erp_integration" featureName="ERP Integration">
            <CustomPlanFeatureRoute requiredFeatures={['erp_integration', 'contract_management']} featureName="Contract Management">
              <PowerOfficeExpenseDetectionPage />
            </CustomPlanFeatureRoute>
          </FeatureRoute></CustomPlanRedirect></ProtectedRoute>
        } />
        <Route path="/erp-integration/business-central/setup" element={
          <ProtectedRoute><CustomPlanRedirect><FeatureRoute feature="erp_integration" featureName="ERP Integration">
            <BusinessCentralSetupPage />
          </FeatureRoute></CustomPlanRedirect></ProtectedRoute>
        } />
        <Route path="/erp-integration/business-central/callback" element={
          <ProtectedRoute><CustomPlanRedirect><FeatureRoute feature="erp_integration" featureName="ERP Integration">
            <BusinessCentralCallbackPage />
          </FeatureRoute></CustomPlanRedirect></ProtectedRoute>
        } />
        <Route path="/erp-integration/business-central/expenses" element={
          <ProtectedRoute><CustomPlanRedirect><FeatureRoute feature="erp_integration" featureName="ERP Integration">
            <CustomPlanFeatureRoute requiredFeatures={['erp_integration', 'contract_management']} featureName="Contract Management">
              <BusinessCentralExpenseDetectionPage />
            </CustomPlanFeatureRoute>
          </FeatureRoute></CustomPlanRedirect></ProtectedRoute>
        } />
        
        {/* Include all admin routes */}
        {AdminRoutes()}
        
        {/* Include API Portal routes */}
        {ApiPortalRoutes()}
        
        {/* Include all route groups */}
        {CategoryRoutes()}
        {ContractRoutes()}
        {ContractTypeRoutes()}
        {CurrencyRoutes()}
        {ContactRoutes()}
        {GroupRoutes()}
        {DocumentRoutes()}
        {ContractDocumentRoutes()}
        {ServiceRoutes()}
        {SupplierRoutes()}
        {GoverningDocumentRoutes()}
        
        {/* Catch-all route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
